// ADR 0023 Sprint 7 Wave B — Platform tenant API.
//
// Fetchers for the /platform/tenants/* endpoints (platform-admin scope).
//
// Path convention: paths passed to apiJson() must NOT include /api/v1.
// VITE_API_BASE_URL=/api/v1 is prepended by buildUrl() in api/client.ts.
//
// Target-tenant-id: every call that operates in a tenant context should
// pass targetTenantId so the backend can validate cross-tenant access.
// The backend derives authorised tenant from the JWT — this header is an
// additional routing hint for multi-tenant platform admins.
//
// Security:
//   - Never log response bodies (may include TenantDetail with login_config).
//   - The backend enforces cross-tenant isolation; frontend sends the header
//     but cannot bypass server-side checks.
import { apiJson } from '@/api/client'
import { targetTenantHeader } from './context'
import type { TenantSummary, TenantDetail, SetupProgress } from './types'

// ---- List tenants ------------------------------------------------------------

export interface ListTenantsResponse {
  items: TenantSummary[]
  total: number
}

/**
 * GET /platform/tenants
 * Returns all tenants visible to the caller (platform-admin scope).
 * Note: Sprint 9 will add pagination params; for now returns all.
 */
export async function listTenants(targetTenantId?: string | null): Promise<ListTenantsResponse> {
  return apiJson<ListTenantsResponse>('/platform/tenants', {
    headers: targetTenantHeader(targetTenantId),
  })
}

// ---- Get one tenant ----------------------------------------------------------

/**
 * GET /platform/tenants/{public_id}
 * Returns the full TenantDetail including profile and login config.
 */
export async function getTenant(
  publicId: string,
  targetTenantId?: string | null,
): Promise<TenantDetail> {
  return apiJson<TenantDetail>(`/platform/tenants/${encodeURIComponent(publicId)}`, {
    headers: targetTenantHeader(targetTenantId),
  })
}

// ---- Create tenant -----------------------------------------------------------

/**
 * POST /platform/tenants
 * Creates a new tenant in draft status.
 * Note: the server forces status=draft; do NOT include status in the body.
 */
export interface CreateTenantInput {
  name: string
  subdomain: string
  country: string
  timezone: string
  currency: string
  /** Optional profile fields. */
  legal_name?: string
  display_name?: string
  short_code?: string
}

export async function createTenant(body: CreateTenantInput): Promise<TenantDetail> {
  return apiJson<TenantDetail>('/platform/tenants', {
    method: 'POST',
    body,
  })
}

// ---- Update tenant profile ---------------------------------------------------

/**
 * Full set of PATCH-able fields on /platform/tenants/{public_id}.
 * Mirrors patchTenantRequest in internal/api/tenant_admin_handler.go.
 *
 * All fields are optional (partial update). Security notes:
 *   - client_secret is write-only: never pre-fill or display.
 *   - login_config fields are NOT returned by GET — they are write-only on PATCH.
 *   - AF-007: contact fields (support_email, support_phone) must never be logged.
 */
export interface UpdateTenantProfileInput {
  /** Core identity */
  name?: string
  country?: string
  timezone?: string
  currency?: string
  /** Profile sub-fields */
  legal_name?: string
  display_name?: string
  short_code?: string
  subdomain?: string
  /** Domain / TLS */
  custom_domain?: string
  /** Login configuration — write-only fields (never pre-filled). */
  issuer_url?: string
  client_id?: string
  /** AF-007: write-only; must not be logged or displayed. */
  client_secret?: string
  allow_password_login?: boolean
  allow_sso_login?: boolean
}

/**
 * PATCH /platform/tenants/{public_id}
 * Partial update to tenant profile, domain, or login-config fields.
 * AF-007: body may include contact/secret fields — never log.
 */
export async function updateTenantProfile(
  publicId: string,
  patch: UpdateTenantProfileInput,
  targetTenantId?: string | null,
): Promise<TenantDetail> {
  return apiJson<TenantDetail>(`/platform/tenants/${encodeURIComponent(publicId)}`, {
    method: 'PATCH',
    body: patch,
    headers: targetTenantHeader(targetTenantId),
  })
}

// ---- Lifecycle mutations (maker-checker gated) --------------------------------

/**
 * POST /platform/tenants/{public_id}/suspend
 * Suspends a tenant. Requires an applied approval_public_id.
 * Backend returns 422 if approval_public_id is missing/unapplied.
 * Backend returns 409 with code "approval_entity_id_mismatch" if the
 * approval entity_id does not match the tenant public_id.
 */
export async function suspendTenant(
  publicId: string,
  approvalPublicId: string,
  targetTenantId?: string | null,
): Promise<TenantDetail> {
  return apiJson<TenantDetail>(`/platform/tenants/${encodeURIComponent(publicId)}/suspend`, {
    method: 'POST',
    body: { approval_public_id: approvalPublicId },
    headers: targetTenantHeader(targetTenantId),
  })
}

/**
 * POST /platform/tenants/{public_id}/archive
 * Archives a tenant. Requires an applied approval_public_id.
 * Same error codes as suspendTenant.
 */
export async function archiveTenant(
  publicId: string,
  approvalPublicId: string,
  targetTenantId?: string | null,
): Promise<TenantDetail> {
  return apiJson<TenantDetail>(`/platform/tenants/${encodeURIComponent(publicId)}/archive`, {
    method: 'POST',
    body: { approval_public_id: approvalPublicId },
    headers: targetTenantHeader(targetTenantId),
  })
}

// ---- Setup progress ----------------------------------------------------------

/**
 * GET /tenants/{tenant_id}/setup-progress
 * Returns the aggregate setup progress for a tenant.
 * tenant_id is the numeric local PK (NOT the public_id UUID).
 */
export async function getSetupProgress(
  tenantId: number,
  targetTenantId?: string | null,
): Promise<SetupProgress> {
  return apiJson<SetupProgress>(`/tenants/${tenantId}/setup-progress`, {
    headers: targetTenantHeader(targetTenantId),
  })
}
